<script setup lang="ts">
import { computed, ref } from 'vue'
import type { GuestTester, GuestTesterInput, MemberCompletionState, UFPMMember } from '../lib/types'
import { guestJerseyMeta, guestLabel, isGuestId } from '../lib/testSession'
import UFPMAddTesterPanel from './UFPMAddTesterPanel.vue'

const HYPOTHETICAL_VALUE = '__hypothetical__'

const props = defineProps<{
  members: readonly UFPMMember[]
  guests: Readonly<Record<string, GuestTester>>
  sessionMemberIds: readonly string[]
  activeMemberId: string | null
  hypotheticalActive: boolean
  completionById?: Readonly<Record<string, MemberCompletionState>>
}>()

const emit = defineEmits<{
  select: [testerId: string]
  hypothetical: []
  'add-member': [member: UFPMMember]
  'add-guest': [input: GuestTesterInput]
}>()

const showAddPanel = ref(false)

interface PoolEntry { id: string; label: string }

const poolEntries = computed<PoolEntry[]>(() =>
  props.sessionMemberIds
    .map((id): PoolEntry | null => {
      if (isGuestId(id)) {
        const g = props.guests[id]
        if (!g) return null
        const meta = guestJerseyMeta(g)
        return { id, label: guestLabel(g) + (meta ? ` · ${meta}` : '') }
      }
      const m = props.members.find(mm => mm.id === id)
      if (!m) return null
      const parts = [m.rank, m.lastName, m.firstName].map(v => String(v ?? '').trim()).filter(Boolean)
      return { id, label: parts.join(' ') || m.fullName || 'Unknown Member' }
    })
    .filter((e): e is PoolEntry => e !== null)
)

function completionPrefix(id: string): string {
  const state = props.completionById?.[id] ?? 'empty'
  if (state === 'saved') return '● '
  if (state === 'draft') return '◐ '
  return '○ '
}

const selectValue = computed(() =>
  props.hypotheticalActive || !props.activeMemberId ? HYPOTHETICAL_VALUE : props.activeMemberId
)

function onSelectChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  if (value === HYPOTHETICAL_VALUE) emit('hypothetical')
  else if (value) emit('select', value)
}

function onAddMember(m: UFPMMember): void {
  emit('add-member', m)
  showAddPanel.value = false
}

function onAddGuest(input: GuestTesterInput): void {
  emit('add-guest', input)
  showAddPanel.value = false
}
</script>

<template>
  <div class="ufpm-calc-member-picker">
    <label class="ufpm-calc-member-picker__label" for="ufpm-calc-member-select">Testing member</label>
    <div class="ufpm-calc-member-picker__row">
      <select
        id="ufpm-calc-member-select"
        class="ufpm-calc-member-select"
        :value="selectValue"
        aria-label="Select testing member"
        @change="onSelectChange"
      >
        <option :value="HYPOTHETICAL_VALUE">Hypothetical — no member</option>
        <option v-for="e in poolEntries" :key="e.id" :value="e.id">
          {{ completionPrefix(e.id) }}{{ e.label }}
        </option>
      </select>
      <button
        class="btn btn-secondary ufpm-calc-member-add-btn"
        type="button"
        @click="showAddPanel = !showAddPanel"
      >+ Add</button>
    </div>

    <div v-if="showAddPanel" class="ufpm-calc-member-add-panel">
      <UFPMAddTesterPanel
        :members="members"
        :session-member-ids="sessionMemberIds"
        @add-member="onAddMember"
        @add-guest="onAddGuest"
      />
    </div>
  </div>
</template>
